import { useQuery } from '@tanstack/react-query';
import type { CustomerMetersResponse, MeterBucket, RegisterChip } from '@metron/shared';
import { serializeChips } from '@metron/shared';
import { useApiClient } from './useApiClient.ts';

export type MeterScope = 'installed' | 'all';

export interface UseCustomerMetersOptions {
  customerId: string | undefined;
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  scope?: MeterScope;
  registerBuckets?: ReadonlyArray<RegisterChip>;
  meterBuckets?: ReadonlyArray<MeterBucket>;
}

export function useCustomerMeters({
  customerId,
  page,
  limit,
  sortBy,
  sortOrder,
  scope = 'installed',
  registerBuckets = [],
  meterBuckets = [],
}: UseCustomerMetersOptions) {
  const apiClient = useApiClient();

  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    scope,
  });
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  const registerCsv = serializeChips(registerBuckets);
  const meterCsv = serializeChips(meterBuckets);
  if (registerCsv) params.set('registerType', registerCsv);
  if (meterCsv) params.set('meterType', meterCsv);

  return useQuery({
    queryKey: [
      'customer-meters',
      customerId,
      page,
      limit,
      sortBy,
      sortOrder,
      scope,
      registerCsv ?? '',
      meterCsv ?? '',
    ],
    queryFn: () =>
      apiClient.get<CustomerMetersResponse>(
        `customers/${customerId}/meters?${params}`,
      ),
    enabled: !!customerId,
  });
}
